/**
 * Viewer permissions for the active Space: loads the effective permission set on
 * space change, exposes membership/admin flags, and supports a client-only role
 * permission preview used by the role editor.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  canAccessSpaceManageUi,
  type createApiClient,
  type SpacePermission,
} from '@adieuu/shared';

type ApiClient = ReturnType<typeof createApiClient>;

export interface RolePermissionPreview {
  roleId: string;
  permissions: SpacePermission[];
}

export interface ActiveSpacePermissionsParams {
  api: ApiClient;
  isLoggedIn: boolean;
  /** Active Space id (undefined/null clears the permission set). */
  activeSpaceId: string | null | undefined;
}

export function useActiveSpacePermissions(params: ActiveSpacePermissionsParams) {
  const { api, isLoggedIn, activeSpaceId } = params;

  const [activeSpacePermissions, setActiveSpacePermissions] = useState<SpacePermission[]>([]);
  const [activeSpaceRoleIds, setActiveSpaceRoleIds] = useState<string[]>([]);
  const [isActiveSpaceAdmin, setIsActiveSpaceAdmin] = useState(false);
  const [activeSpacePermissionsLoading, setActiveSpacePermissionsLoading] = useState(false);
  const [rolePermissionPreview, setRolePermissionPreview] = useState<RolePermissionPreview | null>(
    null,
  );

  /** Synchronously reset all permission state (space cleared / removed). */
  const clearActiveSpacePermissions = useCallback(() => {
    setActiveSpacePermissions([]);
    setActiveSpaceRoleIds([]);
    setIsActiveSpaceAdmin(false);
    setActiveSpacePermissionsLoading(false);
    setRolePermissionPreview(null);
  }, []);

  // Bumped to re-fetch `/me` without switching Spaces (role change, FORBIDDEN).
  const [refreshNonce, setRefreshNonce] = useState(0);
  /** Space id whose permissions fetch last settled. A different id is a switch, not a refresh. */
  const loadedSpaceIdRef = useRef<string | null>(null);
  const refreshActiveSpacePermissions = useCallback(() => {
    setRefreshNonce((n) => n + 1);
  }, []);

  // A role preview belongs to the Space it was opened in.
  useEffect(() => {
    setRolePermissionPreview(null);
  }, [activeSpaceId]);

  // Resolve viewer permissions whenever the active Space changes or a refresh is requested.
  useEffect(() => {
    if (!isLoggedIn || !activeSpaceId) {
      setActiveSpacePermissions([]);
      setActiveSpaceRoleIds([]);
      setIsActiveSpaceAdmin(false);
      setActiveSpacePermissionsLoading(false);
      loadedSpaceIdRef.current = null;
      return;
    }

    let cancelled = false;
    // A background refresh of the same Space keeps the current set visible.
    // Switching Spaces (even after a refresh bumped the nonce) shows loading.
    if (loadedSpaceIdRef.current !== activeSpaceId) setActiveSpacePermissionsLoading(true);
    void api.spaces.getMyPermissions(activeSpaceId).then((res) => {
      if (cancelled) return;
      loadedSpaceIdRef.current = activeSpaceId;
      if (res.success && res.data) {
        setActiveSpacePermissions(res.data.permissions);
        setActiveSpaceRoleIds(res.data.roleIds ?? []);
        setIsActiveSpaceAdmin(res.data.isAdmin);
      } else {
        setActiveSpacePermissions([]);
        setActiveSpaceRoleIds([]);
        setIsActiveSpaceAdmin(false);
      }
      setActiveSpacePermissionsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [api, isLoggedIn, activeSpaceId, refreshNonce]);

  const hasActiveSpacePermission = useCallback(
    (permission: SpacePermission) => {
      if (rolePermissionPreview) {
        return rolePermissionPreview.permissions.includes(permission);
      }
      return activeSpacePermissions.includes(permission);
    },
    [rolePermissionPreview, activeSpacePermissions],
  );

  // Shared list so the gate cannot drift from the Manage sections it guards.
  const canAccessSpaceManage = useMemo(
    () => canAccessSpaceManageUi(activeSpacePermissions),
    [activeSpacePermissions],
  );

  return {
    activeSpacePermissions,
    activeSpaceRoleIds,
    isActiveSpaceAdmin,
    activeSpacePermissionsLoading,
    rolePermissionPreview,
    setRolePermissionPreview,
    hasActiveSpacePermission,
    canAccessSpaceManage,
    clearActiveSpacePermissions,
    refreshActiveSpacePermissions,
  };
}
